import {
  captureAnalyticsEvent,
  setAnalyticsPersonProperties,
} from "@-/dev.improve.haniverse.lib.analytics";
import { getDeviceSetting, setDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  answerOnboardingStateAnnouncement,
  createOnboardingState,
  type OnboardingAnnouncedDestination,
  type OnboardingReaction,
  type OnboardingState,
  playOnboardingStateReaction,
} from "@-/dev.improve.haniverse.lib.onboarding.state";

/** The device's onboarding, or `null` when there is none (see the `onboarding` device setting). */
export function getOnboardingState(): OnboardingState | null {
  return getDeviceSetting("onboarding");
}

function updateOnboardingState(update: (state: OnboardingState) => OnboardingState): void {
  const state = getOnboardingState();
  if (state === null) {
    return;
  }
  const next = update(state);
  if (next !== state) {
    setDeviceSetting("onboarding", next);
  }
}

/**
 * Begins a new learner's onboarding: the welcome gate calls it as a new account leaves it, so the
 * app opens on the globe. Replaces any onboarding a previous account left on this device.
 */
export function startOnboarding(): void {
  setDeviceSetting("onboarding", createOnboardingState(new Date().toISOString()));
  captureAnalyticsEvent("onboarding_started", {});
}

/** An account that signed in (rather than up) gets the whole app, with no onboarding. */
export function clearOnboarding(): void {
  setDeviceSetting("onboarding", null);
}

/** Records `reaction` as played to its end; one already played changes nothing. */
export function playOnboardingReaction(reaction: OnboardingReaction): void {
  updateOnboardingState((state) => {
    const next = playOnboardingStateReaction(state, reaction);
    if (next !== state) {
      captureAnalyticsEvent("onboarding_reaction_played", {
        reaction,
        seconds_since_start: Math.max(
          0,
          Math.round((Date.now() - Date.parse(state.startedAt)) / 1000),
        ),
      });
    }
    return next;
  });
}

/**
 * The learner has answered `destination`'s announcement, by following it or by switching to
 * another tab while it showed: it is not shown again.
 */
export function answerOnboardingAnnouncement(destination: OnboardingAnnouncedDestination): void {
  updateOnboardingState((state) => answerOnboardingStateAnnouncement(state, destination));
}

export type OnboardingAnswers = Pick<
  OnboardingState,
  "dailyGoalMinutes" | "heardAbout" | "reasons"
>;

export function recordOnboardingAnswers(answers: Partial<OnboardingAnswers>): void {
  updateOnboardingState((state) => ({ ...state, ...answers }));
}

/** The questions are done: their answers go to analytics, as option ids only. */
export function completeOnboardingSurvey(): void {
  const state = getOnboardingState();
  if (state === null) {
    return;
  }
  captureAnalyticsEvent("onboarding_survey_completed", {
    daily_goal_minutes: state.dailyGoalMinutes,
    heard_about: state.heardAbout,
    reasons: state.reasons,
  });
  setAnalyticsPersonProperties(
    {
      onboarding_daily_goal_minutes: state.dailyGoalMinutes,
      onboarding_reasons: state.reasons,
    },
    { onboarding_heard_about: state.heardAbout },
  );
}

/** A destination the onboarding unlocked has popped into the strip; it only does so once. */
export function markOnboardingDestinationRevealed(destination: string): void {
  updateOnboardingState((state) =>
    state.revealedDestinations.includes(destination)
      ? state
      : { ...state, revealedDestinations: [...state.revealedDestinations, destination] },
  );
}
